import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useAuthStore } from '@/stores/auth.store'
import type { AccessLevel, Tier } from '@/types/auth.types'

// Convenience aliases (extend as you add role-gated routes)
const ALL: AccessLevel[] = ['access_level.admin', 'access_level.manager', 'access_level.user']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (outside MainLayout)
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      // Shown to authenticated users whose permission tier is `none`.
      // Not public: requires auth; the guard routes `none`-tier sessions here.
      path: '/no-access',
      name: 'no-access',
      component: () => import('@/views/auth/NoPermission.vue'),
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/clienti',
          name: 'clienti',
          component: () => import('@/views/ClientiView.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/performance',
          name: 'performance',
          component: () => import('@/views/Performance.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/incassi',
          name: 'incassi',
          component: () => import('@/views/IncassiView.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/strumenti',
          name: 'strumenti',
          component: () => import('@/views/StrumentiView.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/feedback',
          name: 'feedback',
          component: () => import('@/views/FeedbackView.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/gestione-team',
          name: 'gestione-team',
          component: () => import('@/views/GestioneTeamView.vue'),
          meta: { access_levels: ALL, tiers: ['god', 'manager'] },
        },
        // Add domain-entity routes here — see docs/guides/adding-entities.md
      ],
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Permission tier — 'none' is blocked app-wide (see contract-permissions.md):
  // such users may only see the no-permission screen; everyone else never does.
  const tier = authStore.currentUser?.tier ?? 'none'
  if (tier === 'none') {
    return to.name === 'no-access' ? true : { name: 'no-access' }
  }
  if (to.name === 'no-access') {
    return { name: 'dashboard' }
  }

  // Role-based access check
  const requiredRoles = to.meta.access_levels as AccessLevel[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.access_level)) {
      return { name: 'dashboard' }
    }
  }

  // Tier check. Preferred over the one above: authorization runs on the tier,
  // while `access_level` is rewritten by the personnel import. A route that
  // sets `tiers` cannot be reached by typing its URL.
  const requiredTiers = to.meta.tiers as Tier[] | undefined
  if (requiredTiers && authStore.currentUser) {
    if (!requiredTiers.includes(authStore.currentUser.tier)) {
      return { name: 'dashboard' }
    }
  }

  return true
})

export default router
